import { Currency } from '../enum/currency'
import { SignInMethod } from '../enum/sign-in-method'
import { PaymentMethodType } from '../class/payment-method'
import { ExpiryReminderType } from '../class/expiry-reminder'
import { SessionPriceAlgorithmType } from '../class/session-price-algorithm'
import { UnlockPriceType } from '../class/unlock-price'
import { SlotRefundModeType } from '../class/unused-time-refund'
import {
  objectSchema,
  openUnion,
  optionalTolerant,
  tolerantArray,
  tolerantEnum,
} from '../../_utils/fcdc-v2'
import { settingOf } from './setting.schema'
import * as z from 'zod'

const durationSchema = objectSchema({
  secs: optionalTolerant(z.number()),
  nanos: optionalTolerant(z.number()),
})

const priceSchema = objectSchema({
  currency: tolerantEnum(Currency),
  amount: optionalTolerant(z.union([z.string(), z.number()])),
})

/** API stores VAT as rust_decimal, which often serializes as a string (e.g. "0.25"). */
const vatRateSchema = z
  .union([z.number(), z.string()])
  .transform(value => {
    if (typeof value === 'number') {
      return value
    }
    return parseFloat(value.trim())
  })
  .pipe(z.number())

const visualProfileSchema = objectSchema({
  logoUrl: optionalTolerant(z.string()),
  backgroundColor: optionalTolerant(z.string()),
  primaryColor: optionalTolerant(z.string()),
  secondaryColor: optionalTolerant(z.string()),
})

const rateSchema = objectSchema({
  priceCategoryName: optionalTolerant(z.string()),
  priceCategoryId: optionalTolerant(z.string()),
  currency: tolerantEnum(Currency),
  pricePerAllocatedHour: optionalTolerant(z.string()),
  pricePerUnallocatedHour: optionalTolerant(z.string()),
  pricePerRollingHour: optionalTolerant(z.string()),
})

const gracePeriodSchema = objectSchema({
  duration: optionalTolerant(durationSchema),
  maxUnlocks: optionalTolerant(z.number()),
  usedUnlocks: optionalTolerant(z.number()),
})

const expiryReminderSchema = openUnion('type', {
  [ExpiryReminderType.Free]: objectSchema({
    type: z.literal(ExpiryReminderType.Free),
    startSendingAt: optionalTolerant(z.string()),
    durationUntilExpiry: optionalTolerant(durationSchema),
    sentAt: optionalTolerant(z.string()),
  }),
  [ExpiryReminderType.Paid]: objectSchema({
    type: z.literal(ExpiryReminderType.Paid),
    startSendingAt: optionalTolerant(z.string()),
    durationUntilExpiry: optionalTolerant(durationSchema),
    sentAt: optionalTolerant(z.string()),
    prices: optionalTolerant(tolerantArray(priceSchema)),
  }),
})

const lengthPriceCapSchema = objectSchema({
  blocks: optionalTolerant(
    tolerantArray(
      objectSchema({
        priceCategoryId: optionalTolerant(z.string()),
        length: optionalTolerant(durationSchema),
        priceCap: optionalTolerant(priceSchema),
        repeating: optionalTolerant(z.boolean()),
      })
    )
  ),
})

const unlockPriceSchema = openUnion('type', {
  [UnlockPriceType.Limited]: objectSchema({
    type: z.literal(UnlockPriceType.Limited),
    price: optionalTolerant(priceSchema),
    count: optionalTolerant(z.number()),
  }),
  [UnlockPriceType.Unlimited]: objectSchema({
    type: z.literal(UnlockPriceType.Unlimited),
    price: optionalTolerant(priceSchema),
  }),
})

const paymentMethodSchema = objectSchema({
  type: tolerantEnum(PaymentMethodType),
  providers: optionalTolerant(tolerantArray(z.unknown())),
})

const availableHourSchema = objectSchema({
  startDate: optionalTolerant(z.union([z.string(), z.number()])),
  endDate: optionalTolerant(z.union([z.string(), z.number()])),
  startTime: optionalTolerant(z.union([z.string(), z.number()])),
  endTime: optionalTolerant(z.union([z.string(), z.number()])),
  weekdays: optionalTolerant(z.number()),
  state: optionalTolerant(z.boolean()),
  payload: optionalTolerant(z.unknown()),
})

const priceSlotSchema = objectSchema({
  priceCategoryId: optionalTolerant(z.string()),
  length: optionalTolerant(durationSchema),
  price: optionalTolerant(priceSchema),
})

const priceSlotsSchema = objectSchema({
  slots: optionalTolerant(tolerantArray(priceSlotSchema)),
  overflowSlot: optionalTolerant(tolerantArray(priceSlotSchema)),
})

const sessionPriceAlgorithmSchema = openUnion('type', {
  [SessionPriceAlgorithmType.Rates]: objectSchema({
    type: z.literal(SessionPriceAlgorithmType.Rates),
  }),
  [SessionPriceAlgorithmType.Slots]: objectSchema({
    type: z.literal(SessionPriceAlgorithmType.Slots),
  }),
})

const sharedAccessSchema = objectSchema({
  maxBeneficiaries: optionalTolerant(z.number()),
  basePrices: optionalTolerant(tolerantArray(z.unknown())),
  beneficiaryPrices: optionalTolerant(tolerantArray(z.unknown())),
  excludedCategoryIds: optionalTolerant(z.array(z.string())),
})

const unusedTimeRefundSchema = objectSchema({
  enabled: optionalTolerant(z.boolean()),
  slotRefundMode: optionalTolerant(
    objectSchema({
      type: tolerantEnum(SlotRefundModeType),
    })
  ),
})

const regularRateAfterExpirySchema = objectSchema({
  enabled: optionalTolerant(z.boolean()),
})

export const SETTINGS_KEYS = [
  'acceptedCurrencies',
  'defaultCurrency',
  'vat',
  'termsAndConditionsUrl',
  'privacyPolicyUrl',
  'visualProfile',
  'sessionAllowedSignInMethods',
  'sessionMaxLength',
  'sessionFreePaymentThreshold',
  'gracePeriod',
  'refundGracePeriod',
  'expiryReminder',
  'lockerBaseRates',
  'sessionLengthPriceCap',
  'sessionOverdueLengthPriceCap',
  'unlockPrices',
  'serviceFee',
  'depositionPrice',
  'availableHours',
  'supportedPaymentMethods',
  'lockerBookingAllowed',
  'lockerBookingLeadTime',
  'sessionPriceAlgorithm',
  'sessionPriceSlots',
  'sessionSharedAccess',
  'unusedTimeRefund',
  'regularRateAfterExpiry',
] as const

export const SettingsSchema = objectSchema({
  acceptedCurrencies: optionalTolerant(settingOf(tolerantArray(tolerantEnum(Currency)))),
  defaultCurrency: optionalTolerant(settingOf(tolerantEnum(Currency))),
  vat: optionalTolerant(settingOf(vatRateSchema)),
  termsAndConditionsUrl: optionalTolerant(settingOf(z.string())),
  privacyPolicyUrl: optionalTolerant(settingOf(z.string())),
  visualProfile: optionalTolerant(settingOf(visualProfileSchema)),
  sessionAllowedSignInMethods: optionalTolerant(
    settingOf(tolerantArray(tolerantEnum(SignInMethod)))
  ),
  sessionMaxLength: optionalTolerant(settingOf(durationSchema)),
  sessionFreePaymentThreshold: optionalTolerant(settingOf(tolerantArray(priceSchema))),
  gracePeriod: optionalTolerant(settingOf(gracePeriodSchema)),
  refundGracePeriod: optionalTolerant(settingOf(durationSchema)),
  expiryReminder: optionalTolerant(settingOf(expiryReminderSchema)),
  lockerBaseRates: optionalTolerant(settingOf(tolerantArray(rateSchema))),
  sessionLengthPriceCap: optionalTolerant(settingOf(lengthPriceCapSchema)),
  sessionOverdueLengthPriceCap: optionalTolerant(settingOf(lengthPriceCapSchema)),
  unlockPrices: optionalTolerant(settingOf(tolerantArray(unlockPriceSchema))),
  serviceFee: optionalTolerant(settingOf(tolerantArray(priceSchema))),
  depositionPrice: optionalTolerant(settingOf(tolerantArray(priceSchema))),
  availableHours: optionalTolerant(settingOf(tolerantArray(availableHourSchema))),
  supportedPaymentMethods: optionalTolerant(
    settingOf(tolerantArray(paymentMethodSchema))
  ),
  lockerBookingAllowed: optionalTolerant(settingOf(z.boolean())),
  lockerBookingLeadTime: optionalTolerant(settingOf(durationSchema)),
  sessionPriceAlgorithm: optionalTolerant(settingOf(sessionPriceAlgorithmSchema)),
  sessionPriceSlots: optionalTolerant(settingOf(priceSlotsSchema)),
  sessionSharedAccess: optionalTolerant(settingOf(sharedAccessSchema)),
  unusedTimeRefund: optionalTolerant(settingOf(unusedTimeRefundSchema)),
  regularRateAfterExpiry: optionalTolerant(
    settingOf(regularRateAfterExpirySchema)
  ),
})
